import React, { useState } from 'react';
import classNames from 'classnames';

import { API } from 'constants';
import client from '../../../../services/template-api';

import { useAbortController } from '../../../../hooks/useAbortController';
import { useSwrRequestKeysToMutateFromContext } from '../../../../hooks/useSwrRequestKeysToMutateFromContext';

import { applyOpacityToHex } from '../../../../helpers/applyOpacityToHex';
import { mutateListDataAfterUpdatingItem } from '../../../../helpers/swrMutateUtils';

import { defaultErrorMessage } from '../../../../constants/errorMessages';

import Tag from '../../../../design-system/Tag/Tag';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import LabelEditorModal from '../LabelEditorModal/LabelEditorModal';
import { Badge, SvgIcon } from '../../../../design-system';
import { AddLineIcon, CloseLineIcon } from '../../../../design-system/Icons';

const LabelsSection = ({
    labels,
    setLabels,
    isEditable = false,
    addButtonType = 'ghost',
    shouldSaveChanges = false,
    playbookTemplateId,
}) => {
    const { abortRequest, getSignal, isAbortError } = useAbortController();

    const [actionModal, setActionModal] = useState({ action: null });
    const closeActionModal = () => setActionModal({ action: null });

    const [errorAlert, setErrorAlert] = useState(null);

    const {
        requestKeysToMutate: { playbookTemplates: requestKeysToMutate },
    } = useSwrRequestKeysToMutateFromContext();

    const handleDeleteLabel = async (e, index) => {
        e.stopPropagation();

        if (!shouldSaveChanges) {
            setLabels((prevLabels) => prevLabels.filter((item, i) => i !== index));
        }

        if (shouldSaveChanges) {
            try {
                try {
                    abortRequest();
                    const signal = getSignal();

                    const updatedLabels = labels.filter((item, i) => i !== index);

                    setLabels(updatedLabels);

                    const requestBody = { labels: updatedLabels };
                    const { data } = await client.patch(
                        API.ROUTES.template.playbookTemplate + playbookTemplateId + '/',
                        requestBody,
                        { signal }
                    );

                    mutateListDataAfterUpdatingItem({
                        requestKeysToMutate,
                        id: playbookTemplateId,
                        updatedPart: { labels: data.labels || [], updated_at: data.updated_at },
                    });
                } catch (e) {
                    if (isAbortError(e)) return;

                    setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });

                    const { data } = await client.get(
                        API.ROUTES.template.playbookTemplate + playbookTemplateId + '/'
                    );
                    setLabels(data.labels || []);
                }
            } catch (e) {
                console.log(e);
            }
        }
    };

    const labelClassName = classNames(
        'h-[24px] pl-2 flex items-center gap-2 font-body-medium text-[12px] rounded-2 max-w-full truncate',
        isEditable && 'cursor-pointer pr-1',
        !isEditable && 'pr-2'
    );

    if (!isEditable && !labels?.length) return null;

    return (
        <>
            <div className="w-full max-w-full flex flex-wrap gap-2 overflow-hidden">
                {labels?.map(({ name, icon_color }, index) => (
                    <div
                        key={index}
                        onClick={() => {
                            if (!isEditable) return;
                            setActionModal({
                                action: 'edit',
                                data: { name, icon_color, index },
                            });
                        }}
                        className={labelClassName}
                        style={{
                            color: icon_color,
                            backgroundColor: applyOpacityToHex(icon_color, 20),
                        }}
                    >
                        {name}

                        {isEditable && (
                            <button onClick={async (e) => await handleDeleteLabel(e, index)}>
                                <SvgIcon color="#5E6470" icon={CloseLineIcon} size="medium" />
                            </button>
                        )}
                    </div>
                ))}

                {isEditable && (
                    <button className="block" onClick={() => setActionModal({ action: 'create' })}>
                        {addButtonType === 'ghost' && (
                            <Tag
                                text="Label"
                                leadingIcon={AddLineIcon}
                                leadingIconColor="#5E6470"
                                type="ghost"
                            />
                        )}
                        {addButtonType === 'neutral' && (
                            <Badge
                                text="Label"
                                leadingIcon={AddLineIcon}
                                leadingIconColor="#5E6470"
                            />
                        )}
                    </button>
                )}
            </div>

            {(actionModal.action === 'create' || actionModal.action === 'edit') && (
                <LabelEditorModal
                    mode={actionModal.action}
                    data={actionModal.data}
                    allLabels={labels || []}
                    setLabels={setLabels}
                    shouldSaveChanges={shouldSaveChanges}
                    playbookTemplateId={playbookTemplateId}
                    onClose={closeActionModal}
                />
            )}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </>
    );
};

export default LabelsSection;
